import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, AlertCircle, Share2 } from 'lucide-react';
import TacticalReport from '../components/TacticalReport';
import FeedbackWidget from '../components/FeedbackWidget';
import { getAnalysisById } from '../services/api';

const AnalysisPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [shareSuccess, setShareSuccess] = useState(false);

  // ✅ FIXED: loadAnalysis is now properly defined with useCallback
  const loadAnalysis = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Calling getAnalysisById with id:', id);
      const response = await getAnalysisById(id);
      console.log('🔍 FULL API RESPONSE:', JSON.stringify(response, null, 2));

      if (response.success) {
        setAnalysis(response.analysis);
      } else {
        throw new Error(response.message || 'Failed to load analysis');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading analysis:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAnalysis();
  }, [loadAnalysis]);

  const handleShare = async () => {
    try {
      const shareUrl = `${window.location.origin}/analysis/${id}`;
      
      if (navigator.share) {
        await navigator.share({
          title: `${t('app.name')} Analysis - ${analysis.opponent_team}`,
          text: `Tactical analysis for ${analysis.opponent_team}`,
          url: shareUrl
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Share error:', err);
    }
  };

  const handleDownload = () => {
    alert('PDF export coming soon! This will generate a comprehensive tactical report.');
  };

  const handleFeedback = (rating) => {
    setShowFeedback(true);
  };

  const handleFeedbackSuccess = () => {
    setShowFeedback(false);
  };

  const handleBack = () => {
    navigate('/history');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('common.notFound')}
            </h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={handleBack}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              {t('analysis.backToHistory')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={handleBack}
            className="flex items-center space-x-2 rtl:space-x-reverse text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">{t('analysis.backToHistory')}</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Share Success Message */}
        {shareSuccess && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3 rtl:space-x-reverse">
            <Share2 className="h-5 w-5 text-green-600" />
            <p className="text-sm text-green-800 font-medium">
              Link copied to clipboard!
            </p>
          </div>
        )}

        {/* Tactical Report */}
        <TacticalReport
          analysisData={analysis}
          onShare={handleShare}
          onDownload={handleDownload}
          onFeedback={handleFeedback}
        />

        {/* Feedback Modal */}
        {showFeedback && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <FeedbackWidget
              analysisId={id}
              onClose={() => setShowFeedback(false)}
              onSubmitSuccess={handleFeedbackSuccess}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalysisPage;
